import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ScanFace, Swords, Trophy, BookOpen, ArrowUpRight, Flame, Sparkles } from "lucide-react";

const QUICK = [
  { to: "/scan", icon: ScanFace, label: "New Scan", sub: "AI facial analysis", testid: "quick-scan" },
  { to: "/coach", icon: Sparkles, label: "AI Coach", sub: "Looks + pharma expert", testid: "quick-coach" },
  { to: "/battle", icon: Swords, label: "Mog Battle", sub: "1v1 head-to-head", testid: "quick-battle" },
  { to: "/leaderboard", icon: Trophy, label: "Leaderboard", sub: "Global ELO ladder", testid: "quick-leaderboard" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [scans, setScans] = useState([]);

  useEffect(() => {
    api.get("/scans/me").then((r) => setScans(r.data)).catch(() => {});
  }, []);

  const latest = scans[0];

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="grid grid-cols-12 gap-6 mb-10">
        <div className="col-span-12 md:col-span-8">
          <span className="tape-label">CONTROL ROOM</span>
          <h1 className="font-display text-5xl md:text-7xl mt-3 leading-none">
            WELCOME BACK,<br />
            <span className="text-[var(--primary)]">@{user?.username}.</span>
          </h1>
          <p className="text-[var(--text-muted)] mt-4 max-w-lg">
            The ladder doesn't climb itself. Run a scan, fight a battle, or pull a protocol.
          </p>
        </div>
        <div className="col-span-12 md:col-span-4 grid grid-cols-3 gap-px bg-[var(--border)]">
          <StatTile label="ELO" value={user?.elo ?? 1000} />
          <StatTile label="W / L" value={`${user?.wins ?? 0}/${user?.losses ?? 0}`} />
          <StatTile label="BEST" value={user?.best_score ?? 0} accent />
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-12 gap-px bg-[var(--border)] mb-10">
        {QUICK.map(({ to, icon: Icon, label, sub, testid }) => (
          <Link
            key={to}
            to={to}
            data-testid={testid}
            className="col-span-12 md:col-span-6 lg:col-span-3 bg-[var(--bg)] p-6 group hover:bg-[var(--surface)] transition-colors flex items-start justify-between"
          >
            <div>
              <Icon className="w-6 h-6 text-[var(--primary)] mb-4" />
              <div className="font-display text-2xl tracking-wide group-hover:text-[var(--primary)] transition-colors">{label}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">{sub}</div>
            </div>
            <ArrowUpRight className="w-4 h-4 text-[var(--text-muted)] group-hover:text-[var(--primary)] transition-colors" />
          </Link>
        ))}
      </div>

      {/* Latest scan + history */}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-7 panel p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="tape-label">LATEST DIAGNOSTIC</span>
            <Link to="/scan" className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-white" data-testid="run-new-scan-link">
              Run new scan →
            </Link>
          </div>
          {latest ? (
            <Link to={`/scan/${latest.id}`} className="block" data-testid="latest-scan-card">
              <div className="flex items-end gap-4 mb-4">
                <span className="font-display text-7xl text-white">{latest.overall_score}</span>
                <div className="text-xs font-mono text-[var(--text-muted)] pb-3">
                  <div>TIER: <span className="text-[var(--primary)]">{latest.analysis?.tier ?? "—"}</span></div>
                  <div>SCANNED {new Date(latest.created_at).toLocaleString()}</div>
                </div>
              </div>
              <p className="text-sm text-[var(--text-muted)] leading-relaxed line-clamp-3">{latest.analysis?.verdict}</p>
            </Link>
          ) : (
            <div className="border border-dashed border-[var(--border-bright)] p-8 text-center">
              <Flame className="w-8 h-8 text-[var(--primary)] mx-auto mb-3" />
              <div className="font-display text-2xl mb-2">NO SCANS YET</div>
              <p className="text-xs font-mono text-[var(--text-muted)] uppercase tracking-widest mb-5">Upload your first selfie to get rated</p>
              <Link to="/scan" className="btn-brutal" data-testid="empty-state-scan-button">Run first scan →</Link>
            </div>
          )}
        </div>

        <div className="col-span-12 lg:col-span-5 panel p-6">
          <span className="tape-label">SCAN HISTORY</span>
          <div className="mt-4 space-y-px bg-[var(--border)]">
            {scans.length === 0 && (
              <div className="bg-[var(--surface)] p-4 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">No history yet</div>
            )}
            {scans.slice(0, 6).map((s) => (
              <Link
                key={s.id}
                to={`/scan/${s.id}`}
                data-testid={`history-${s.id}`}
                className="bg-[var(--surface)] hover:bg-[var(--surface-2)] p-3 flex items-center justify-between transition-colors"
              >
                <div className="flex items-center gap-4">
                  <span className="font-display text-3xl text-white w-12">{s.overall_score}</span>
                  <div>
                    <div className="text-xs font-mono text-white">{s.analysis?.tier ?? "Scan"}</div>
                    <div className="text-[10px] font-mono text-[var(--text-muted)]">{new Date(s.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-[var(--text-muted)]" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const StatTile = ({ label, value, accent }) => (
  <div className="bg-[var(--bg)] p-4">
    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{label}</div>
    <div className={`font-display text-3xl mt-1 ${accent ? "text-[var(--primary)]" : "text-white"}`}>{value}</div>
  </div>
);
